import type { IBatchStore } from './batch-store-types';
import { DeviceTypes } from '../../constants/DeviceModels';
import { DeviceStatus } from '../../constants/DeviceStatus';
import {
  RemoteBatchData,
  RemoteControlEventType,
} from '../../containers/Batch/RemoteBatchController';
import {
  asyncForEach,
  waitFor,
} from '../../utils/async';
import logger from '../../utils/logger';

export const loadManagementMixin = {

  /**
   * Resume single load
   *
   * @param batchDataId
   * @param drop
   */
  resumeLoad(this: IBatchStore, batchDataId: any, drop = 1): void {
    if (this.canSendRemoteInstruction()) {
      this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.RESUME_LOAD, null, {
        id: batchDataId,
        drop,
      }));
      return;
    }

    const batchData = this.running.find((v: any) => v.id === batchDataId && v.currentDrop === drop);

    if (!batchData) {
      return;
    }

    batchData.isHoldLoad = false;

    if (this.trackedFeedPumps.length) {
      this.trackedFeedPumps.forEach((id: any) => {
        const s = this.getDeviceStatus(id);
        if (s.uid === batchData.uid && s.currentDrop === batchData.currentDrop) {
          this.resumeFeedPump(id);
        }
      });
    }

    if (this.holdFeedDevices.length) {
      this.holdFeedDevices.forEach((id: any) => {
        const s = this.getDeviceStatus(id);
        if (s.uid === batchData.uid && s.currentDrop === batchData.currentDrop) {
          this.holdFeedDevices = this.holdFeedDevices.filter((_id: any) => _id !== id);
        }
      });
    }

    if (this.trackedDischargeScales) {
      this.trackedDischargeScales.forEach((id: any) => {
        if (this.isActiveDrop(batchData)) {
          this.resumeScale(id);
        }
        this.trackedDischargeScales = this.trackedDischargeScales.filter((_id: any) => _id !== id);
      });
    }
  },

  /**
   * Hold single load
   *
   * @param batchDataId
   * @param drop
   */
  holdLoad(this: IBatchStore, batchDataId: any, drop = 1): void {
    if (this.canSendRemoteInstruction()) {
      this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.HOLD_LOAD, null, {
        id: batchDataId,
        drop,
      }));
      return;
    }

    const batchData = this.running.find((v: any) => v.id === batchDataId && v.currentDrop === drop);

    if (!batchData) {
      return;
    }

    batchData.isHoldLoad = true;

    this.deviceStatus.forEach((v: any) => {
      if (!this.holdFeedDevices.includes(v.id)
        && (v.status === DeviceStatus.FEED || v.status === DeviceStatus.JOG)
        && (v.type === DeviceTypes.Bin.toUpperCase() || v.type === DeviceTypes.Pump.toUpperCase())
        && (v.uid === batchData.uid && v.currentDrop === batchData.currentDrop)
      ) {
        this.holdFeedDevices.push(v.id);
      }
    });
  },

  /**
   * Execute remove of a single load
   *
   * @param batchDataId
   * @param drop
   */
  executeRemoveLoad(this: IBatchStore, batchDataId: any, drop = 1): void {
    const batchData = this.running.find((v: any) => v.id === batchDataId && v.currentDrop === drop);

    if (!batchData) {
      return;
    }

    logger.log(`removing load: ${batchData.tId} ${batchData.currentDrop}/${batchData.allDrops}`);

    this.deviceStatus.forEach((v: any) => {
      if (v.uid && v.uid === batchData.uid && v.currentDrop === batchData.currentDrop) {
        this.deviceStatus.set(v.id, this.getDeviceStatusDefault(v));
        this.trackedFeedPumps = this.trackedFeedPumps.filter((id: any) => id !== v.id);
        this.holdFeedDevices = this.holdFeedDevices.filter((id: any) => id !== v.id);
        this.trackedDischargeScales = this.trackedDischargeScales.filter((id: any) => id !== v.id);
      } else if (v.status !== DeviceStatus.IDLE && v.status !== DeviceStatus.FINISH && this.running.length === 1) {
        this.deviceStatus.set(v.id, this.getDeviceStatusDefault(v));
      }
    });

    const lastLoad = { ...this.running[0] };

    if (lastLoad.uid === batchData.uid && lastLoad.currentDrop === batchData.currentDrop) {
      this.discharging = false;
    }

    this.waitingForDischarge = this.waitingForDischarge.filter((v: any) => v.uid !== batchData.uid || v.currentDrop !== batchData.currentDrop);
    this.finishBatch({ ...batchData, isAborted: true });
  },

  removeFromQueue(this: IBatchStore, id: any): void {
    if (this.canSendRemoteInstruction()) {
      this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.REMOVE_FROM_QUEUE, null, { id }));
      return;
    }

    this.queue = this.queue.filter((d: any) => d.id !== id);
    this.ticketStore.revertTicketFromQueue(id);
  },

  setInitialData(this: IBatchStore, clearAllDeviceStatus = true): void {
    if (clearAllDeviceStatus) {
      this.deviceStatus = new Map();
    } else {
      this.clearDeviceStatus();
    }
    this.waitingForDischarge = [];
    this.trackedDischargeScales = [];
    this.trackedFeedPumps = [];
    this.holdFeedDevices = [];
    this.finishFeedDevices = [];
    this.pressedBinButtons.clear();
    this.running = [];
    this.scaleQueue = {};
    this.discharging = false;
    this.isDischarge = false;
    this.isAbort = false;
    this.isHoldFeed = false;
    this.isHoldDischarge = false;
    this.isOverride = false;
    this.loadHistory = [];
    this.stoppedDevices = [];
  },

  finishAllLoads(this: IBatchStore): Promise<any> {
    const start = async () => {
      await asyncForEach(this.running, async (data: any, i: number) => {
        this.finishBatch({ ...data, isAborted: true });
        logger.log('Finish all loads', i, data);
        await waitFor(200);
      });
    };

    return start();
  },

  executeAbort(this: IBatchStore, abortWithQueue?: boolean): void {
    abortWithQueue ??= this.abortWithQueue;
    this.isAbort = true;

    if (abortWithQueue) {
      this.queue = [];
    }

    this.abortWithQueue = true;

    if (this.canSendRemoteInstruction()) {
      this.sendRemoteInstruction(RemoteBatchData(RemoteControlEventType.ABORT, null, { abortWithQueue }));
      return;
    }

    if (this.isDemo) {
      this.pacSimulator.reset();
    }

    this.sendAbortCommands();

    this.finishAllLoads().then(() => {

      this.clearAllTimers();
      this.setInitialData(abortWithQueue);

      if (this.isDemo) {
        this.pacSimulator.init();
        this.pacSimulator.reset();
      }

    });
  },

};
